import type { AdjustmentSuggestionView, HabitCategory, HabitWeekReport, WeekStart } from '@contracts';
import { HeartPulse, Sparkles, BookOpen, CircleEllipsis } from '@ui/shared/icons';

const CATEGORY_ICON: Record<HabitCategory, typeof HeartPulse> = {
  health: HeartPulse,
  self_care: Sparkles,
  study: BookOpen,
  other: CircleEllipsis,
};

const CATEGORY_TINT_VAR: Record<HabitCategory, string> = {
  health: 'var(--accent-health)',
  self_care: 'var(--accent-selfcare)',
  study: 'var(--accent-study)',
  other: 'var(--accent-other)',
};

type SuggestingHabit = HabitWeekReport & { readonly suggestion: AdjustmentSuggestionView };

/**
 * Suggestions panel, phone (desktop-weekly-review.md §9.4) — already a
 * single-column list of inner cards on desktop, so the same shape works at
 * phone width with no restructuring. The one real change: Accept/Keep stack
 * full-width (Accept above Keep) instead of sitting side by side — two
 * 44pt-min buttons side by side at ~326px would each be too narrow for their
 * label plus the inline XP suffix without wrapping. Content/copy/XP values
 * identical to the desktop `SuggestionPanel`.
 */
export function SuggestionPanel({
  suggestions,
  adjustmentAcceptedXp,
  onAccept,
  onDecline,
}: {
  readonly suggestions: readonly SuggestingHabit[];
  readonly adjustmentAcceptedXp: number;
  readonly onAccept: (suggestion: AdjustmentSuggestionView) => Promise<void>;
  readonly onDecline: (habitId: string, reviewWeek: WeekStart) => void;
}) {
  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--layout-card-padding-phone)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
        Suggestions
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        {suggestions.map((habit) => (
          <SuggestionCard
            key={habit.habitId}
            habit={habit}
            adjustmentAcceptedXp={adjustmentAcceptedXp}
            onAccept={onAccept}
            onDecline={onDecline}
          />
        ))}
      </div>
    </section>
  );
}

function SuggestionCard({
  habit,
  adjustmentAcceptedXp,
  onAccept,
  onDecline,
}: {
  readonly habit: SuggestingHabit;
  readonly adjustmentAcceptedXp: number;
  readonly onAccept: (suggestion: AdjustmentSuggestionView) => Promise<void>;
  readonly onDecline: (habitId: string, reviewWeek: WeekStart) => void;
}) {
  const Icon = CATEGORY_ICON[habit.category];
  const adjustment = habit.suggestion.adjustment;
  const isBuild = adjustment.habitKind === 'build';
  const proposedLabel = isBuild
    ? `${adjustment.next.kind === 'daily' ? 'Daily' : adjustment.next.targetPerWeek}×`
    : `${adjustment.next.limitPerWeek}`;
  const currentLabel = isBuild
    ? adjustment.previous.kind === 'daily'
      ? 'Daily'
      : `${adjustment.previous.targetPerWeek}×`
    : `${adjustment.previous.limitPerWeek}`;

  const headline = isBuild
    ? `${habit.name}: planned ${currentLabel}, done ${habit.current.actual}× two weeks running.`
    : `${habit.name}: allowance ${currentLabel}, logged over allowance two weeks running.`;

  const rationale = isBuild
    ? 'A target you hit is worth more than a target you chase.'
    : 'Meeting a realistic limit beats missing a strict one.';

  const acceptLabel = `Adjust to ${proposedLabel} — +${adjustmentAcceptedXp} XP`;
  const keepLabel = `Keep at ${currentLabel}`;

  return (
    <div
      style={{
        background: 'var(--surface-overlay)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
        <span
          aria-hidden="true"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `color-mix(in srgb, ${CATEGORY_TINT_VAR[habit.category]} 14%, transparent)`,
            flex: 'none',
          }}
        >
          <Icon size={16} color={CATEGORY_TINT_VAR[habit.category]} aria-hidden="true" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>{headline}</p>
          <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>{rationale}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            <button
              type="button"
              onClick={() => void onAccept(habit.suggestion)}
              style={{
                minHeight: 'var(--touch-target-min)',
                padding: '0 var(--space-4)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--hue-primary-vivid)',
                color: 'var(--text-on-accent)',
                fontWeight: 600,
                fontSize: 'var(--type-body-size)',
                border: '1px solid var(--hue-primary-strong)',
                width: '100%',
              }}
            >
              {acceptLabel}
            </button>
            <button
              type="button"
              onClick={() => onDecline(habit.habitId, habit.suggestion.reviewWeek)}
              style={{
                minHeight: 'var(--touch-target-min)',
                padding: '0 var(--space-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-secondary)',
                fontWeight: 500,
                fontSize: 'var(--type-body-size)',
                background: 'none',
                width: '100%',
              }}
            >
              {keepLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
